import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { Lsos } from 'src/apis/lng-marketing/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  url: string;
  label: string;
  fieldName: string;
};

export const lsoOptionSchema = z
  .object({
    hint: z.string().optional(),
    // Links saved before February 2026 stored the short name as `shortName`.
    shortName: z.string().optional(),
    ...formOptionSchema.shape,
  })
  .transform((option, ctx) => {
    const hint = option.hint ?? option.shortName;
    if (hint === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['hint'],
        message: 'Invalid input: expected string, received undefined',
        fatal: true,
      });
      return z.NEVER;
    }
    return { label: option.label, value: option.value, hint };
  });

type LsoOption = z.output<typeof lsoOptionSchema>;

export const LsoSearchableMultiSelect: FC<Props> = ({
  url,
  label,
  fieldName,
}) => {
  const lsos = useAxios(
    (axios, baseConfig) =>
      axios.request<Lsos>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  useEffect(() => {
    lsos.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  const lsosSearch = useClientSearch(
    lsos.response?.data._embedded.items.map(
      (lso) =>
        ({
          label: lso.name,
          value: lso.id,
          hint: lso.shortName,
        }) satisfies LsoOption
    ),
    (lso, search) =>
      lso.label.toLowerCase().match(search) ||
      lso.hint.toLowerCase().match(search)
  );

  return (
    <SearchableMultiSelect
      label={label}
      name={fieldName}
      stacked
      placeholder="E.g. LSO-1"
      {...lsosSearch}
    />
  );
};
